import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Card,
  CardBody,
  Badge,
  IconButton,
  useColorModeValue,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Center,
  useToast,
  useBreakpointValue
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FiPlay, FiClock, FiMoreVertical, FiX } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { removeFromWatchlist } from '../services/watchlistService';
import { useWatchlistStream } from '../hooks/useWatchlistStream';
import { getUserBookLibrary, type BookLibraryItem } from '../services/bookLibraryService';
import { getContentById } from '../services/contentService';

const Watchlist: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  
  // Use StreamBuilder hook for real-time watchlist data
  const { items: watchlistItems, loading: watchlistLoading, error: watchlistError } = useWatchlistStream(user?.uid || null);
  const [libraryBooks, setLibraryBooks] = useState<Array<{ contentId: string; content?: any; addedDate?: string; status?: string; progress?: number }>>([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  
  // Load books from bookLibrary collection
  useEffect(() => {
    if (!user?.uid) {
      setLibraryBooks([]);
      return;
    }
    
    setLoadingBooks(true);
    const unsubscribe = getUserBookLibrary(user.uid, async (books: BookLibraryItem[]) => {
      try {
        const bookItems = await Promise.all(
          books.map(async (book) => {
            try {
              const content = await getContentById(book.bookId);
              return {
                contentId: book.bookId,
                content: content,
                addedDate: book.addedAt ? new Date(book.addedAt).toISOString() : new Date().toISOString(),
                status: 'plan-to-watch',
                progress: 0
              };
            } catch (error) {
              console.error(`Error loading book ${book.bookId}:`, error);
              return {
                contentId: book.bookId,
                content: {
                  id: book.bookId,
                  title: book.bookTitle,
                  thumbnail: book.bookCover,
                  category: 'book'
                },
                addedDate: book.addedAt ? new Date(book.addedAt).toISOString() : new Date().toISOString(),
                status: 'plan-to-watch',
                progress: 0
              };
            }
          })
        );
        setLibraryBooks(bookItems);
      } catch (error) {
        console.error('Error loading library books:', error);
      } finally {
        setLoadingBooks(false);
      }
    });
    
    return () => unsubscribe();
  }, [user?.uid]);
  
  // Combine watchlist and library items
  const allItems = useMemo(() => {
    const combined = [...watchlistItems];
    // Add library books that aren't already in watchlist
    libraryBooks.forEach(book => {
      if (!combined.find(item => item.contentId === book.contentId)) {
        combined.push(book);
      }
    });
    return combined;
  }, [watchlistItems, libraryBooks]);
  
  const loading = watchlistLoading || loadingBooks;
  const error = watchlistError;
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const titleColor = useColorModeValue('gray.900', 'white');
  const progressTextColor = useColorModeValue('gray.800', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Show error if there's an issue loading watchlist
  if (error) {
    toast({
      title: 'Error',
      description: error,
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'watching': return 'blue';
      case 'completed': return 'green';
      case 'plan-to-watch': return 'purple';
      case 'dropped': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'watching': return 'Watching';
      case 'completed': return 'Completed';
      case 'plan-to-watch': return 'Plan to Watch';
      case 'dropped': return 'Dropped';
      default: return 'Unknown';
    }
  };

  // Memoized filtered items for performance
  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      const title = item.content?.title || item.title || 'Untitled';
      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || item.content?.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [allItems, searchTerm, filterCategory]);

  const handleRemoveFromWatchlist = async (contentId: string) => {
    if (!user) return;
    
    try {
      await removeFromWatchlist(user.uid, contentId);
      toast({
        title: 'Removed from watchlist',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove from watchlist',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getTabItems = (status: string) => {
    return filteredItems.filter(item => item.status === status);
  };

  if (loading) {
    return (
      <Center minH="50vh">
        <Spinner size="xl" color="purple.400" />
      </Center>
    );
  }

  return (
    <Box maxW="1200px" mx="auto" p={{ base: 4, md: 6 }}>
      <VStack gap={{ base: 4, md: 8 }} align="stretch">
        <VStack align="start" gap={{ base: 3, md: 4 }}>
          <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={titleColor}>
            My Watchlist
          </Text>
          
          <VStack gap={3} w="100%" align="stretch">
            <InputGroup w="100%" maxW={{ base: '100%', md: '400px' }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search your watchlist..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg={inputBg}
              />
            </InputGroup>
            
            <Select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              w={{ base: '100%', md: '200px' }}
              bg={inputBg}
            >
              <option value="all">All Types</option>
              <option value="movie">Movies</option>
              <option value="series">Series</option>
              <option value="book">Books & Comics</option>
            </Select>
          </VStack>
        </VStack>

        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple">
          <TabList 
            overflowX="auto" 
            overflowY="hidden"
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            pb={2}
          >
            <Tab 
              fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
              px={{ base: 2, sm: 3, md: 4 }}
              py={{ base: 2, sm: 3 }}
              whiteSpace="nowrap"
              minW="fit-content"
            >
              All ({filteredItems.length})
            </Tab>
            <Tab 
              fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
              px={{ base: 2, sm: 3, md: 4 }}
              py={{ base: 2, sm: 3 }}
              whiteSpace="nowrap"
              minW="fit-content"
            >
              {isMobile ? 'Watch' : 'Watching'} ({getTabItems('watching').length})
            </Tab>
            <Tab 
              fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
              px={{ base: 2, sm: 3, md: 4 }}
              py={{ base: 2, sm: 3 }}
              whiteSpace="nowrap"
              minW="fit-content"
            >
              Done ({getTabItems('completed').length})
            </Tab>
            <Tab 
              fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
              px={{ base: 2, sm: 3, md: 4 }}
              py={{ base: 2, sm: 3 }}
              whiteSpace="nowrap"
              minW="fit-content"
            >
              {isMobile ? 'Planned' : 'Plan to Watch'} ({getTabItems('plan-to-watch').length})
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
                {filteredItems.map((item) => {
                  const title = item.content?.title || item.title || 'Untitled';
                  const category = item.content?.category || 'unknown';
                  const thumbnail = item.content?.thumbnail || item.content?.poster;
                  
                  return (
                    <GridItem key={item.contentId}>
                      <Card bg={cardBg} borderColor={borderColor} overflow="hidden">
                        <Box position="relative">
                          <Box
                            width="100%"
                            height="250px"
                            bgImage={thumbnail ? `url(${thumbnail})` : undefined}
                            bgSize="cover"
                            bgPosition="center"
                            bg="gray.700"
                          />
                          
                          <Box
                            position="absolute"
                            top={2}
                            right={2}
                            display="flex"
                            gap={1}
                          >
                            <Badge colorScheme={getStatusColor(item.status || 'plan-to-watch')}>
                              {getStatusText(item.status || 'plan-to-watch')}
                            </Badge>
                          </Box>
                          
                          <Box
                            position="absolute"
                            bottom={2}
                            left={2}
                            right={2}
                            bg="blackAlpha.700"
                            borderRadius="md"
                            p={2}
                          >
                            <HStack justify="space-between" align="center">
                              <Text fontSize="xs" color={progressTextColor}>
                                {item.progress || 0}% watched
                              </Text>
                              <HStack gap={1}>
                                <IconButton
                                  aria-label="Play"
                                  icon={<IconWrapper icon={FiPlay} size={16} />}
                                  size="sm"
                                  variant="ghost"
                                  color={progressTextColor}
                                />
                                <IconButton
                                  aria-label="More options"
                                  icon={<IconWrapper icon={FiMoreVertical} size={16} />}
                                  size="sm"
                                  variant="ghost"
                                  color={progressTextColor}
                                />
                              </HStack>
                            </HStack>
                          </Box>
                        </Box>
                        
                        <CardBody p={4}>
                          <VStack align="start" gap={2}>
                            <Text fontWeight="bold" fontSize="lg" noOfLines={1}>
                              {title}
                            </Text>
                            
                            <HStack gap={2} flexWrap="wrap">
                              <Badge colorScheme="purple" variant="subtle">
                                {category}
                              </Badge>
                              <Text fontSize="xs" color={labelColor}>
                                Added {item.addedDate ? new Date(item.addedDate).toLocaleDateString() : 'Recently'}
                              </Text>
                            </HStack>
                            
                            <HStack gap={2} w="100%">
                              <Button
                                size="sm"
                                colorScheme="purple"
                                flex="1"
                                leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                              >
                                {(item.progress || 0) > 0 ? 'Continue' : 'Start'}
                              </Button>
                              <IconButton
                                aria-label="Remove from watchlist"
                                icon={<IconWrapper icon={FiX} size={16} />}
                                size="sm"
                                variant="outline"
                                onClick={() => handleRemoveFromWatchlist(item.contentId)}
                              />
                            </HStack>
                          </VStack>
                        </CardBody>
                      </Card>
                    </GridItem>
                  );
                })}
              </Grid>
            </TabPanel>

            {['watching', 'completed', 'plan-to-watch'].map((status) => (
              <TabPanel key={status} px={0}>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
                  {getTabItems(status).map((item) => {
                    const title = item.content?.title || item.title || 'Untitled';
                    const category = item.content?.category || 'unknown';
                    const thumbnail = item.content?.thumbnail || item.content?.poster;
                    
                    return (
                      <GridItem key={item.contentId}>
                        <Card bg={cardBg} borderColor={borderColor} overflow="hidden">
                          <Box position="relative">
                            <Box
                              width="100%"
                              height="250px"
                              bgImage={thumbnail ? `url(${thumbnail})` : undefined}
                              bgSize="cover"
                              bgPosition="center"
                              bg="gray.700"
                            />
                            
                            <Box
                              position="absolute"
                              bottom={2}
                              left={2}
                              right={2}
                              bg="blackAlpha.700"
                              borderRadius="md"
                              p={2}
                            >
                              <HStack justify="space-between" align="center">
                                <Text fontSize="xs" color={progressTextColor}>
                                  {item.progress || 0}% watched
                                </Text>
                                <HStack gap={1}>
                                  <IconButton
                                    aria-label="Play"
                                    icon={<IconWrapper icon={FiPlay} size={16} />}
                                    size="sm"
                                    variant="ghost"
                                    color={progressTextColor}
                                  />
                                  <IconButton
                                    aria-label="More options"
                                    icon={<IconWrapper icon={FiMoreVertical} size={16} />}
                                    size="sm"
                                    variant="ghost"
                                    color={progressTextColor}
                                  />
                                </HStack>
                              </HStack>
                            </Box>
                          </Box>
                          
                          <CardBody p={4}>
                            <VStack align="start" gap={2}>
                              <Text fontWeight="bold" fontSize="lg" noOfLines={1}>
                                {title}
                              </Text>
                              
                              <HStack gap={2} flexWrap="wrap">
                                <Badge colorScheme="purple" variant="subtle">
                                  {category}
                                </Badge>
                                <Text fontSize="xs" color={labelColor}>
                                  Added {item.addedDate ? new Date(item.addedDate).toLocaleDateString() : 'Recently'}
                                </Text>
                              </HStack>
                              
                              <HStack gap={2} w="100%">
                                <Button
                                  size="sm"
                                  colorScheme="purple"
                                  flex="1"
                                  leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                                >
                                  {(item.progress || 0) > 0 ? 'Continue' : 'Start'}
                                </Button>
                                <IconButton
                                  aria-label="Remove from watchlist"
                                  icon={<IconWrapper icon={FiX} size={16} />}
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRemoveFromWatchlist(item.contentId)}
                                />
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      </GridItem>
                    );
                  })}
                </Grid>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>

        {filteredItems.length === 0 && (
          <Center py={12}>
            <VStack gap={4}>
              <IconWrapper icon={FiClock} size={64} />
              <Text fontSize="lg" color={placeholderColor}>
                {searchTerm || filterCategory !== 'all' 
                  ? 'No items match your search criteria'
                  : 'Your watchlist is empty'
                }
              </Text>
              <Text fontSize="sm" color={labelColor} textAlign="center">
                {searchTerm || filterCategory !== 'all'
                  ? 'Try adjusting your search or filter'
                  : 'Start adding movies, series, and books to your watchlist'
                }
              </Text>
            </VStack>
          </Center>
        )}
      </VStack>
    </Box>
  );
};

export default Watchlist;
